import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, CalendarDays, MapPin, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, fmtDate, brl, LEVELS, STAGE_STATUS, fileUrl } from "../lib/api";
import { PageHeader, Loader, Empty, Field } from "../components/common";
import { ImageUpload } from "../components/ImageUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const blank = { name: "", number: "", date: "", location: "", description: "", price: 0, levels: LEVELS, status: "inscricoes_abertas", cover_path: null, season_id: "" };

export const StageForm = ({ open, onOpenChange, stage, onSaved }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [seasons, setSeasons] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/seasons").then((r) => {
      setSeasons(r.data);
      const act = (r.data.find((s) => s.active) || {}).id || "";
      setF(stage ? { ...blank, ...stage, season_id: stage.season_id || act } : { ...blank, season_id: act });
    });
  }, [open, stage]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggleLevel = (l) => setF({ ...f, levels: f.levels.includes(l) ? f.levels.filter((x) => x !== l) : LEVELS.filter((x) => f.levels.includes(x) || x === l) });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, number: f.number ? Number(f.number) : null, price: Number(f.price || 0) };
      const { data } = stage ? await api.put(`/stages/${stage.id}`, body) : await api.post("/stages", body);
      toast.success(stage ? "Etapa atualizada" : "Etapa criada");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-lg">
        <DialogHeader><DialogTitle className="h-display text-3xl">{stage ? "Editar etapa" : "Nova etapa"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-[1fr_90px] gap-3">
            <Field label="Nome"><input required data-testid="stage-name-input" className="inp" value={f.name} onChange={set("name")} placeholder="1ª Etapa - Verão" /></Field>
            <Field label="Nº"><input data-testid="stage-number-input" type="number" className="inp" value={f.number || ""} onChange={set("number")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Data"><input data-testid="stage-date-input" type="date" className="inp" value={f.date} onChange={set("date")} /></Field>
            <Field label="Valor inscrição (por atleta)"><input data-testid="stage-price-input" type="number" step="0.01" className="inp" value={f.price} onChange={set("price")} /></Field>
          </div>
          <Field label="Local"><input data-testid="stage-location-input" className="inp" value={f.location} onChange={set("location")} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select data-testid="stage-status-select" className="inp" value={f.status} onChange={set("status")}>
                {Object.entries(STAGE_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Temporada">
              <select data-testid="stage-season-select" className="inp" value={f.season_id || ""} onChange={set("season_id")}>
                {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Níveis disponíveis">
            <div className="flex flex-wrap gap-2">
              {LEVELS.map((l) => (
                <button type="button" key={l} data-testid={`stage-level-${l}`} onClick={() => toggleLevel(l)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold ${f.levels.includes(l) ? "bg-[#E4F222] text-black" : "border border-[#232B3E] text-[#94A3B8]"}`}>{l}</button>
              ))}
            </div>
          </Field>
          <Field label="Descrição"><textarea data-testid="stage-description-input" rows={3} className="inp" value={f.description} onChange={set("description")} /></Field>
          <Field label="Capa"><ImageUpload wide value={f.cover_path} onChange={set("cover_path")} testid="stage-cover-upload" label="Enviar capa" /></Field>
          <button disabled={busy} data-testid="stage-save-btn" className="btn-lime w-full rounded-full py-3">{busy ? "Salvando..." : "Salvar etapa"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const StageStatus = ({ status }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${status === "inscricoes_abertas" ? "bg-[#E4F222] text-black" : status === "em_andamento" ? "bg-sky-500/20 text-sky-300" : "bg-[#1A202E] text-[#94A3B8]"}`}>
    {STAGE_STATUS[status]}
  </span>
);

export default function Stages() {
  const { isAdmin } = useAuth();
  const [stages, setStages] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/stages").then((r) => setStages(r.data));
  useEffect(() => { load(); }, []);
  if (!stages) return <Loader />;
  return (
    <div>
      <PageHeader eyebrow="Circuito" title="Etapas" subtitle="Cada evento é uma etapa do circuito."
        action={isAdmin && <button data-testid="new-stage-btn" onClick={() => setOpen(true)} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Nova etapa</button>} />
      {stages.length === 0 ? <Empty icon={CalendarDays} title="Nenhuma etapa" text="Crie a primeira etapa do circuito." /> : (
        <div className="grid gap-4 md:grid-cols-2">
          {stages.map((s, i) => (
            <Link key={s.id} to={`/app/etapas/${s.id}`} data-testid={`stage-card-${s.id}`} className={`card-x card-hover relative overflow-hidden p-5 rise rise-${(i % 4) + 1}`}>
              {s.cover_path && <img src={fileUrl(s.cover_path)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />}
              <div className="relative">
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl font-black italic text-[#E4F222]/90">#{s.number}</span>
                  <StageStatus status={s.status} />
                </div>
                <div className="h-display mt-2 text-2xl">{s.name}</div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#94A3B8]">
                  <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDate(s.date)}</span>
                  {s.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.location}</span>}
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{s.total_teams} duplas</span>
                  {s.price > 0 && <span>{brl(s.price)}/atleta</span>}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">{s.levels.map((l) => <span key={l} className="rounded-full bg-[#0B0E14] px-2 py-0.5 text-[10px] text-[#94A3B8]">{l} · {s.counts[l] || 0}</span>)}</div>
                  <ChevronRight className="h-5 w-5 text-[#E4F222]" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      <StageForm open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}
